import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../../../store/store';
import { useCheckProcessExists } from '../../../queries/use-check-process-exists';
import { useForwardDocumentsMutation } from '../../../queries/useForwardDocumentsMutation';
import { useSendMailMutation } from '../../../queries/useSendMailMutation';
import { useCheckRequestKeyMutation } from '../../../queries/useCheckRequestKeyMutation';
import { useExecuteProcess, useCheckProcessStatus } from '../../authentication/api';

type SubmissionState = 'initial' | 'loading' | 'success' | 'error';

export const useBackgroundSubmission = () => {
    const hasStartedSubmission = useRef(false);
    
    const [submissionState, setSubmissionState] = useState<SubmissionState>('initial');

    const { userData, destinationKey, sendingFlow, loginFlow, gjeldsConsent } = useStore((state) => ({
        userData: state.userData,
        destinationKey: state.destinationKey,
        sendingFlow: state.sendingFlow,
        loginFlow: state.loginFlow,
        gjeldsConsent: state.gjeldsConsent,
    }));

    const checkProcessExists = useCheckProcessExists();
    const executeProcess = useExecuteProcess();
    const checkProcessStatus = useCheckProcessStatus();
    const forwardDocumentsMutation = useForwardDocumentsMutation();
    const sendMailMutation = useSendMailMutation();
    const checkRequestKeyMutation = useCheckRequestKeyMutation();

    const handleCRMSubmission = useCallback(async (processId: string) => {
        try {
            await forwardDocumentsMutation.mutateAsync({
                data: {
                    processId,
                    requestKey: destinationKey,
                },
            });

            // Check request key status every 5 seconds
            const checkRequestKeyInterval = setInterval(async () => {
                const { state } = await checkRequestKeyMutation.mutateAsync({
                    requestKey: destinationKey,
                });

                if (state === 'Finished') {
                    setSubmissionState('success');
                    clearInterval(checkRequestKeyInterval);
                }

                if (
                    state === 'Closed' ||
                    state === 'NotFound' ||
                    state === 'Unknown'
                ) {
                    setSubmissionState('error');
                    clearInterval(checkRequestKeyInterval);
                }
            }, 5000);

            // Timeout after 10 minutes
            setTimeout(() => {
                clearInterval(checkRequestKeyInterval);
                setSubmissionState('error');
            }, 600000);
        } catch (error) {
            console.error('CRM submission error:', error);
            setSubmissionState('error');
        }
    }, [destinationKey, forwardDocumentsMutation, checkRequestKeyMutation]);

    const handleEmailSubmission = useCallback(async (processId: string) => {
        try {
            await sendMailMutation.mutateAsync({
                data: {
                    processId,
                    destination: destinationKey,
                },
            });
            setSubmissionState('success');
        } catch (error) {
            console.error('Email submission error:', error);
            setSubmissionState('error');
        }
    }, [destinationKey, sendMailMutation]);

    const waitForDataCollection = useCallback(async (processId: string): Promise<void> => {
        /**
         * If the login flow is BANKID then we need to wait a fixed time,
         * since check process will always return 'Created' for BANKID.
         * For BANKID_MOBILE, we poll the status every 5 seconds.
         */
        if (loginFlow === 'BANKID') {
            // Fixed 15 second wait for BANKID
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve();
                }, 15000);
            });
        }

        // For BANKID_MOBILE: Poll process status every 5 seconds
        return new Promise((resolve, reject) => {
            const checkStatusInterval = setInterval(async () => {
                try {
                    const { state, errorCode } = await checkProcessStatus.mutateAsync({
                        processId,
                    });

                    if (state === 'Finished') {
                        clearInterval(checkStatusInterval);
                        resolve();
                    }

                    if (
                        state === 'Closed' ||
                        state === 'NotFound' ||
                        state === 'Unknown' ||
                        state === 'Error'
                    ) {
                        clearInterval(checkStatusInterval);
                        reject(new Error(`Process failed with state: ${state}, errorCode: ${errorCode}`));
                    }
                } catch (error) {
                    clearInterval(checkStatusInterval);
                    reject(error);
                }
            }, 5000);

            // Timeout after 10 minutes
            setTimeout(() => {
                clearInterval(checkStatusInterval);
                reject(new Error('Data collection timeout after 10 minutes'));
            }, 600000);
        });
    }, [loginFlow, checkProcessStatus]);

    const createOrGetProcess = useCallback(async (): Promise<string> => {
        // Check if process exists
        const processExists = await checkProcessExists.mutateAsync({
            personNumber: userData.personNumber,
        });

        if (processExists && processExists.data) {
            // Use existing process
            return processExists.data.processId;
        } else {
            // Create new process
            return await executeProcess.mutateAsync({
                data: {
                    userData: {
                        personNumber: userData.personNumber,
                        phoneNumber: userData.phoneNumber,
                    },
                    destinationKey,
                    gjeldsConsent,
                    loginFlow,
                    sendingFlow,
                },
            });
        }
    }, [
        userData.personNumber,
        userData.phoneNumber,
        destinationKey,
        gjeldsConsent,
        loginFlow,
        sendingFlow,
        checkProcessExists,
        executeProcess,
    ]);

    const performSubmission = useCallback(async (processId: string) => {
        if (sendingFlow === 'CRM') {
            await handleCRMSubmission(processId);
        } else if (sendingFlow === 'EMAIL') {
            await handleEmailSubmission(processId);
        } else {
            setSubmissionState('success');
        }
    }, [sendingFlow, handleCRMSubmission, handleEmailSubmission]);

    const startBackgroundSubmission = useCallback(async () => {
        if (hasStartedSubmission.current) return;
        hasStartedSubmission.current = true;

        setSubmissionState('loading');

        // Add a small delay before starting submission
        setTimeout(async () => {
            try {
                // Step 1: Create or get process
                const processId = await createOrGetProcess();
                
                // Step 2: Wait for backend data collection to complete
                await waitForDataCollection(processId);
                
                // Step 3: Perform submission after data is ready
                await performSubmission(processId);
            } catch (error) {
                console.error('Background submission error:', error);
                setSubmissionState('error');
            }
        }, 1000);
    }, [createOrGetProcess, waitForDataCollection, performSubmission]);

    useEffect(() => {
        if (submissionState === 'initial' && userData.personNumber) {
            startBackgroundSubmission();
        }
    }, [submissionState, userData.personNumber, startBackgroundSubmission]);

    return {
        submissionState,
    };
};
